import type { EngagementCategory } from "../../hooks/useEngagementFeed";
import { resolveSupportedLocale, type SupportedLocale } from "../../lib/locale";

type PlayoffInsightCopy = {
  title: string;
  loading: string;
  error: string;
  retry: string;
  empty: string;
  typeLabels: Record<EngagementCategory | "UPDATE", string>;
  pulseLabels: {
    recap: string;
    next: string;
    pulse: string;
  };
};

const copyByLocale: Record<SupportedLocale, PlayoffInsightCopy> = {
  en: {
    title: "Competition Pulse",
    loading: "Loading live updates...",
    error: "Could not load updates right now.",
    retry: "Retry",
    empty: "No new playoff updates right now.",
    typeLabels: {
      UPCOMING_GAME: "Upcoming",
      YESTERDAY_RECAP: "Recap",
      BREAKING_NEWS: "Breaking",
      INJURY_UPDATE: "Injury",
      GAME_UPDATE: "Live",
      RESULT: "Result",
      UPSET: "Upset",
      ELIMINATED: "Eliminated",
      ADVANCING: "Advancing",
      SERIES_MOMENTUM: "Momentum",
      SCHEDULE: "Schedule",
      CHAMPION: "Champion",
      UPDATE: "Update",
    },
    pulseLabels: {
      recap: "Recap",
      next: "Next",
      pulse: "Pulse",
    },
  },
  fr: {
    title: "Pouls de la compétition",
    loading: "Chargement des mises à jour...",
    error: "Impossible de charger les mises à jour pour le moment.",
    retry: "Réessayer",
    empty: "Aucune nouvelle mise à jour des éliminatoires.",
    typeLabels: {
      UPCOMING_GAME: "À venir",
      YESTERDAY_RECAP: "Récap",
      BREAKING_NEWS: "Dernière",
      INJURY_UPDATE: "Blessure",
      GAME_UPDATE: "Direct",
      RESULT: "Résultat",
      UPSET: "Surprise",
      ELIMINATED: "Éliminé",
      ADVANCING: "Qualifié",
      SERIES_MOMENTUM: "Élan",
      SCHEDULE: "Horaire",
      CHAMPION: "Champion",
      UPDATE: "Mise à jour",
    },
    pulseLabels: {
      recap: "Récap",
      next: "Prochain",
      pulse: "Pouls",
    },
  },
  ja: {
    title: "大会パルス",
    loading: "最新情報を読み込み中...",
    error: "現在、最新情報を読み込めません。",
    retry: "再試行",
    empty: "新しいプレーオフ情報はありません。",
    typeLabels: {
      UPCOMING_GAME: "予定",
      YESTERDAY_RECAP: "振り返り",
      BREAKING_NEWS: "速報",
      INJURY_UPDATE: "負傷",
      GAME_UPDATE: "ライブ",
      RESULT: "結果",
      UPSET: "番狂わせ",
      ELIMINATED: "敗退",
      ADVANCING: "進出",
      SERIES_MOMENTUM: "勢い",
      SCHEDULE: "日程",
      CHAMPION: "優勝",
      UPDATE: "更新",
    },
    pulseLabels: {
      recap: "要約",
      next: "次戦",
      pulse: "動き",
    },
  },
  es: {
    title: "Pulso de la competencia",
    loading: "Cargando actualizaciones...",
    error: "No se pudieron cargar las actualizaciones ahora.",
    retry: "Reintentar",
    empty: "No hay nuevas actualizaciones de playoffs ahora.",
    typeLabels: {
      UPCOMING_GAME: "Próximo",
      YESTERDAY_RECAP: "Resumen",
      BREAKING_NEWS: "Última hora",
      INJURY_UPDATE: "Lesión",
      GAME_UPDATE: "En vivo",
      RESULT: "Resultado",
      UPSET: "Sorpresa",
      ELIMINATED: "Eliminado",
      ADVANCING: "Avanza",
      SERIES_MOMENTUM: "Impulso",
      SCHEDULE: "Calendario",
      CHAMPION: "Campeón",
      UPDATE: "Actualización",
    },
    pulseLabels: {
      recap: "Resumen",
      next: "Próximo",
      pulse: "Pulso",
    },
  },
};

export function getPlayoffInsightCopy(locale: string | null | undefined): PlayoffInsightCopy {
  return copyByLocale[resolveSupportedLocale(locale)];
}

export function labelForPlayoffInsightType(type: EngagementCategory, locale: string | null | undefined): string {
  const copy = getPlayoffInsightCopy(locale);
  return copy.typeLabels[type] ?? copy.typeLabels.UPDATE;
}

export function pulseLabelForPlayoffInsightType(type: EngagementCategory, locale: string | null | undefined): string {
  const copy = getPlayoffInsightCopy(locale);
  if (type === "RESULT" || type === "YESTERDAY_RECAP") return copy.pulseLabels.recap;
  if (type === "UPCOMING_GAME" || type === "SCHEDULE") return copy.pulseLabels.next;
  return copy.pulseLabels.pulse;
}
